<template>
  <div class="store v2">
    <Spinner :spin="isLoading" />
    <!-- Main -->
    <section class="main" v-if="portalInfo">
      <!-- Card -->
      <section class="card">
        <div class="top">
          <span class="title">{{ portalInfo.title }}</span>
          <div v-if="portalInfo.logo">
            <img :src="portalInfo.logo" />
          </div>
        </div>
        <!-- 會員驗證 -->
        <VerificationButton v-if="userInfo.phoneVerified === 0" />
        <!-- 選單 -->
        <Slider
          id="menus"
          class="menus"
          :speed="500"
          :autoplay="false"
          :indicators="false"
          :control-btn="menuPages > 1"
          width="100%"
          height="100%"
          v-model="menuSliderIndex"
          :before-previous="checkMovePrev"
          :before-next="checkMoveNext"
          :style="`min-height: ${menus.length > 4 ? '126' : '60'}px`"
        >
          <SliderItem
            v-for="(page, pageIndex) in menuPages"
            :key="'page_' + pageIndex"
            class="menu"
          >
            <!-- 功能每頁八個 -->
            <a
              v-for="(menu, index) in getMenusInPage(pageIndex + 1)"
              :key="'menu_' + index"
              @click.prevent="menu.clickHandler"
            >
              <font-awesome-icon
                :icon="menu.icon"
                size="xl"
              ></font-awesome-icon>
              <div v-if="menu.name === '通知'" class="notice">
                <span>{{ unreadCount }}</span>
              </div>
              <span>{{ menu.name }}</span>
            </a>
          </SliderItem>
          <div slot="loading"></div>
        </Slider>
        <!-- Carousel -->
        <Slider
          v-if="portalInfo.banners && portalInfo.banners.length"
          id="carousel"
          class="carousel"
          :speed="500"
          :interval="5000"
          :control-btn="false"
          width="100%"
          height="180px"
        >
          <SliderItem
            v-for="(banner, index) in portalInfo.banners"
            :key="'banner' + index"
          >
            <a @click="openExternalUrl(banner.externalUrl)"
              ><img :src="banner.imageUrl"
            /></a>
          </SliderItem>
        </Slider>
      </section>
      <!-- Tabs -->
      <section class="tabs">
        <div class="bg"></div>
        <fragment v-if="tabs.length > 1">
          <a
            v-if="hasPointsTab"
            :class="{ active: tab === 'points' }"
            @click="tab = 'points'"
          >
            <img src="@/assets/icon/pointW.svg" />
            <!-- <span>點數 <span> {{ displayedPointBalance }} </span></span> -->
            <div v-if="pointsInitialized">
              <span class="tab__name">點數</span>
              <span class="tab__count"> {{ displayedPointBalance }} </span>
            </div>
            <div v-else>
              <div class="tab__loading"></div>
            </div>
          </a>
          <a
            v-if="hasTicketsTab"
            :class="{ active: tab === 'tickets' }"
            @click="tab = 'tickets'"
          >
            <img src="@/assets/icon/ticketW.svg" />
            <!-- <span>優惠券 <span>{{ tickets.length }}</span></span> -->
            <div v-if="ticketInitialized">
              <span class="tab__name"> 優惠券 </span>
              <span class="tab__count"> {{ userTickets.length }} </span>
            </div>
            <div v-else>
              <div class="tab__loading"></div>
            </div>
          </a>
          <a
            v-if="hasStampsTab"
            :class="{ active: tab === 'stamps' }"
            @click="tab = 'stamps'"
          >
            <img src="@/assets/icon/stampW.svg" />
            <!-- <span> 集點卡 <span>{{ stampCards.length }}</span></span> -->
            <div>
              <span class="tab__name"> 集點卡 </span>
              <span class="tab__count"> {{ stampCards.length }} </span>
            </div>
            <!-- <div>
              <div class="tab__loading"></div>
            </div> -->
          </a>
        </fragment>
      </section>
      <!-- 點金券 Loading -->
      <template v-if=" (tab === 'points' && !pointsInitialized) || (tab === 'tickets' && !ticketInitialized)">
        <div class="loading transparent loading--body"><div>
          <img v-if="portalInfo.logo" :src="portalInfo.logo">
        <div></div></div></div>
      </template>
      
      <!-- 點數 -->
      <section class="point" v-if="tab === 'points' && pointsInitialized">
        <a class="header" @click="goTo('/point-record')">
          <div>
            <div><img src="@/assets/icon/point.svg" /></div>
            <span class="title">{{ pointName }}</span>
            <span>{{ displayedPointBalance }}</span>
          </div>
          <span v-if="displayedPointPeriods"
            >{{ displayedPointPeriods.pointBalance }}點 將於
            {{ tsToDate(displayedPointPeriods.endTs) }}到期</span
          >
        </a>
        <!-- 點數紀錄預覽最多3筆 -->
        <div
          class="list"
          v-for="(transaction, index) in displayedTransactions"
          :key="'transaction' + index"
        >
          <div>
            <span>{{ transaction.note }}</span>
            <span>{{ tsToDatetime(transaction.transactionTs) }}</span>
          </div>
          <span
            >{{ displaySignOfPoint(transaction.entryType)
            }}{{ transaction.pointAmount }}</span
          >
        </div>
        <!-- 按鈕 -->
        <div class="action">
          <a @click="goTo('/point-record')">
            <font-awesome-icon icon="fa-solid fa-list-ul"></font-awesome-icon>
            <span>交易紀錄</span>
          </a>
          <a v-if="pointDescription" @click="pointRulePopupOpen = true">
            <font-awesome-icon
              icon="fa-solid fa-circle-info"
            ></font-awesome-icon>
            <span>點數說明</span>
          </a>
        </div>
        <!-- 點數規則popup -->
        <PointRulePopup
          v-if="pointRulePopupOpen"
          @closePopup="pointRulePopupOpen = false"
        />
      </section>
      <!-- 優惠券 -->
      <section class="coupon" v-if="tab === 'tickets' && ticketInitialized">
        <a class="list" @click="goTo('/tickets')">
          <font-awesome-icon
            icon="fa-solid fa-list-ul"
            size="lg"
          ></font-awesome-icon>
          <span>兌換紀錄</span>
        </a>
        <a v-if="ticketInitialized && !userTickets.length" class="coupon">
          <div class="dot left"></div>
          <div class="dot right"></div>
          <span class="none">暫無優惠券</span>
        </a>
        <a
          v-else
          class="coupon"
          v-for="(ticket, index) in userTickets"
          :key="'ticket_' + index"
          @click="goTo(`/tickets/${ticket.ticketId}`)"
        >
          <div class="dot left"></div>
          <div class="dot right"></div>
          <div class="content">
            <div>
              <span class="title">{{ ticket.title }}</span>
              <span class="name">{{ ticket.storeName }}</span>
            </div>
            <span class="info" v-html="ticket.description"></span>
            <span v-if="ticket.duration"> {{ ticket.duration }}</span>
            <span v-else class="date"
              >{{ tsToDate(ticket.useStartAt) }} -
              {{ tsToDate(ticket.useExpirationAt) }}</span
            >
          </div>
          <img v-if="ticket.image" :src="ticket.image" />
        </a>
      </section>
      <!-- 集章 -->
      <StampCards
        v-if="tab === 'stamps'"
        :stampCards="stampCards"
        :selectedStampCard="selectedStampCard"
        @setSelectedStampCard="setSelectedStampCard"
        :stamp="stampInfo"
        @updateStamp="getStamp"
      />
    </section>

    <!-- 通知popup -->
    <NotificationPopup
      v-if="notificationPopupOpen"
      @closePopup="notificationPopupOpen = false"
    />
    <!-- 會員條碼popup -->
    <BarcodePopup
      v-if="barcodePopupOpen"
      @closePopup="barcodePopupOpen = false"
    />
    <!-- 邀請禮popup -->
    <InvitePopup v-if="invitePopupOpen" @closePopup="invitePopupOpen = false" />
  </div>
</template>
<script>
import moment from "moment-timezone";
import { mapState, mapGetters } from "vuex";
import { Slider, SliderItem } from "vue-easy-slider";
import BarcodePopup from "@/components/BarcodePopup.vue";
import NotificationPopup from "@/components/NotificationPopup.vue";
import PointRulePopup from "@/components/PointRulePopup.vue";
import InvitePopup from "@/components/InvitePopup.vue";
import Spinner from "@/components/Spinner.vue";
import VerificationButton from "@/components/VerificationButton.vue";
import StampCards from "@/components/StampCards.vue";

export default {
  name: "Home",
  components: {
    BarcodePopup,
    NotificationPopup,
    Slider,
    SliderItem,
    Spinner,
    PointRulePopup,
    InvitePopup,
    VerificationButton,
    StampCards,
  },
  beforeRouteEnter(to, from, next) {
    to.meta.from = from;
    next();
  },
  data() {
    return {
      apiHost: process.env.VUE_APP_API_HOST,
      tab: "",
      // transactions: {},
      // tickets: [],
      selectedStampCard: {},
      stampInfo: {},
      barcodePopupOpen: false,
      notificationPopupOpen: false,
      invitePopupOpen: false,
      pointRulePopupOpen: false,
      ticketInitialized: false,
      pointsInitialized: false,
      stampInitialized: false,
      isLoading: false,
      menuSliderIndex: 0,
    };
  },
  computed: {
    ...mapState(["user", "userInfo"]),
    ...mapState("portal", ["portalInfo"]),
    ...mapGetters({
      userPoints: "getUserPoints",
      userTickets: "getUserTickets",
      transactions: "getPointTransactions",
      unreadCount: "noti/unreadCount",
      needUpdateStampCards: "noti/needUpdateStampCards"
    }),
    merchantId() {
      return this.user.user?._json?.merchantId || this.userInfo.merchantId;
    },
    displayedPointBalance() {
      return this.userPoints?.pointBalance || 0;
    },
    displayedPointPeriods() {
      return this.userPoints?.pointPeriods
        ?.filter((p) => p.endTs * 1000 > Date.now())
        .sort((a, b) => a.endTs - b.endTs)[0];
    },
    displayedTransactions() {
      return this.transactions?.records?.slice(0, 3) || [];
    },
    pointName() {
      return this.portalInfo?.wallet?.points?.name;
    },
    menus() {
      const services = this.portalInfo?.design?.services?.map((service) => {
        let clickHandler;
        switch (service.key) {
          case "barcodes":
            clickHandler = () => (this.barcodePopupOpen = true);
            break;
          case "stores":
          case "articles":
          case "profile":
            clickHandler = () => this.goTo(`/${service.key}`);
            break;
          case "notifications":
            clickHandler = () => (this.notificationPopupOpen = true);
            break;
          case "mgm":
            clickHandler = () => (this.invitePopupOpen = true);
            break;
          default:
            clickHandler = () => {
              if (!service.url) return;
              window.open(service.url, "_blank");
            };
        }

        return {
          ...service,
          clickHandler,
        };
      });
      return services || [];
    },
    menuPages() {
      return Math.ceil(this.menus.length / 8);
    },
    tabs() {
      if (!this.portalInfo?.wallet) return [];
      return Object.keys(this.portalInfo.wallet).filter(
        (tab) => this.portalInfo.wallet[tab]?.enabled
      );
    },
    hasPointsTab() {
      return this.portalInfo?.wallet?.points?.enabled;
    },
    hasTicketsTab() {
      return this.portalInfo?.wallet?.tickets?.enabled;
    },
    hasStampsTab() {
      return this.portalInfo?.wallet?.stamps?.enabled;
    },
    stampCards() {
      return this.portalInfo?.wallet?.stamps?.stampCards || [];
    },
    pointDescription() {
      return (
        this.portalInfo.wallet?.points?.settings?.loyaltyProgram?.description
      );
    },
  },
  mounted() {
    this.hideMenusPrevArrow();
  },
  activated() {
    if (this.hasPointsTab) {
      this.getUserPoints().then(() => {
        if (this.userPoints.pointCode) {
          this.getTransactions();
        }
      });
    }

    if (this.hasTicketsTab) {
      this.getTickets();
    }
  },
  deactivated() {
    this.closePopup();
  },
  watch: {
    menuSliderIndex(index) {
      index === 0 ? this.hideMenusPrevArrow() : this.showMenusPrevArrow();
      index === this.menuPages - 1
        ? this.hideMenusNextArrow()
        : this.showMenusNextArrow();
    },
    tabs: {
      immediate: true,
      handler() {
        this.tab = this.tabs.length ? this.tabs[0] : "";
      },
    },
    stampCards: {
      immediate: true,
      handler() {
        this.selectedStampCard = this.stampCards.length
          ? this.stampCards[0]
          : {};
      },
    },
    selectedStampCard: {
      immediate: true,
      handler() {
        this.selectedStampCard.stampCardId && this.getStamp();
      },
    },
    needUpdateStampCards() {
      if (this.needUpdateStampCards) {
        this.getStamp();
        this.$store.commit("noti/setNeedUpdateStampCards", false);
      }
    },
  },
  methods: {
    goTo(path) {
      this.$router.push({ path, query: this.$route.query });
    },
    checkMovePrev() {
      return this.menuSliderIndex !== 0;
    },
    checkMoveNext() {
      return this.menuSliderIndex !== this.menuPages - 1;
    },
    tsToDatetime(ts) {
      return moment(ts * 1000).format("YYYY-MM-DD HH:mm");
    },
    tsToDate(ts) {
      return moment(ts * 1000).format("YYYY/MM/DD");
    },
    getMenusInPage(page) {
      return this.menus.slice((page - 1) * 8, page * 8);
    },
    closePopup() {
      this.barcodePopupOpen = false;
      this.notificationPopupOpen = false;
      this.pointRulePopupOpen = false;
      this.invitePopupOpen = false;
    },
    showMenusPrevArrow() {
      const prevArrow = menus?.querySelector(".slider-btn-left");
      if (prevArrow) prevArrow.style.display = "block";
    },
    hideMenusPrevArrow() {
      const prevArrow = menus?.querySelector(".slider-btn-left");
      if (prevArrow) prevArrow.style.display = "none";
    },
    showMenusNextArrow() {
      const nextArrow = menus?.querySelector(".slider-btn-right");
      if (nextArrow) nextArrow.style.display = "block";
    },
    hideMenusNextArrow() {
      const nextArrow = menus?.querySelector(".slider-btn-right");
      if (nextArrow) nextArrow.style.display = "none";
    },
    displaySignOfPoint(entryType) {
      return entryType == "C" ? "-" : "";
    },
    openExternalUrl(externalUrl) {
      if (externalUrl) {
        window.open(externalUrl, "_blank");
      }
    },
    getUserPoints() {
      var config = {
        method: "get",
        url: `${this.apiHost}/points/v1/merchants/${this.merchantId}/members/${this.user.userId}/points`,
        headers: {},
      };
      return this.$http(config)
        .then((res) => {
          this.$store.commit("updateUserPoints", res.data);
        })
        .catch((err) => {
          console.log("getUserPoints err:", err);
        })
        .finally(() => {
          this.pointsInitialized = true;
        });
    },
    callGetStampAPI() {
      let config = {
        url: `${this.apiHost}/stamps/v1/merchants/${this.merchantId}/collectors/${this.user.userId}/stamp-cards/${this.selectedStampCard.stampCardId}/collector-status`,
      };
      return this.$http(config);
    },
    getStamp() {
      this.callGetStampAPI()
        .then((res) => {
          this.stampInfo = res.data;
          // 更新點券, TBC： 此時點券資訊可能不會更新，目前MM最多就只能顯示10張....
          this.$nextTick(() => {
            if (this.hasPointsTab) {
            this.getUserPoints().then(() => {
              if (this.userPoints.pointCode) {
                this.getTransactions();
              }
            });
          }

          if (this.hasTicketsTab) {
            this.getTickets();
          }
          })
          
        })
        .catch((err) => {
          console.log(err);
        });
    },
    callTransactionsAPI() {
      let endTime = Math.floor(Date.now() / 1000);
      let startTime = endTime - 1 * 60 * 60 * 24 * 90; //3個月

      var config = {
        method: "get",
        url: `${this.apiHost}/points/v1/merchants/${this.merchantId}/members/${this.user.userId}/points/${this.userPoints.pointCode}/ledgers?from_ts=${startTime}&to_ts=${endTime}`,
        headers: {},
      };
      return this.$http(config);
    },
    getTransactions() {
      this.callTransactionsAPI()
        .then((res) => {
          // this.transactions = res.data;
          console.log("getTransactionsResData: ", res.data);
          this.$store.commit('updatePointTransactions', res.data);
        })
        .catch((err) => {
          console.log("getTransactions err: ", err);
        });
    },
    callGetTicketsAPI() {
      let url = `${this.apiHost}/tickets/v1/merchants/${this.merchantId}/members/${this.user.userId}/tickets`;
      var config = {
        method: "get",
        url: url,
        headers: {},
      };
      return this.$http(config);
    },
    dummyTickets() {
      let result = [
        {
          merchantId: "milkhouses",
          saleOrderId: "230540050467809",
          productId: 9,
          name: "巴布羅吐司免費兌換1條",
          title: "巴布羅吐司免費兌換券",
          description:
            "本電子票券可於11月22日數位轉型成果展活動當日，於米哥烘焙坊櫃位，免費兌換巴布羅吐司一條，限活動期間，現場兌換，數量有限，換完為止。",
          restriction:
            "<ol>\r\n<li>請出示優惠券條碼，供現場門市人員掃描後方可兌換。</li>\r\n</ol>",
          images: [
            "https://storage.googleapis.com/qc_staging_products/library/temp/mh002.jpg",
          ],
          storeId: 102,
          storeName: "米哥烘焙坊 - 民生門市",
          storeCategoryId: 1,
          storeCategory: "餐飲",
          productType: "ticket",
          provider: null,
          useStartAt: 1667836800,
          useExpirationAt: 1669089600,
          orderCreatedTs: 1677084646,
          tickets: [
            {
              ticketId: 727,
              barcodeMessage: "230540050467809000",
              status: "redeemed",
              createdTs: 1677084646,
              updatedTs: 1677086363,
            },
            {
              ticketId: 728,
              barcodeMessage: "230540105163584000",
              status: "redeemed",
              createdTs: 1677085516,
              updatedTs: 1677086423,
            },
            {
              ticketId: 729,
              barcodeMessage: "230540105163584001",
              status: "redeemed",
              createdTs: 1677085516,
              updatedTs: 1677086487,
            },
            {
              ticketId: 730,
              barcodeMessage: "230540105163584002",
              status: "redeemed",
              createdTs: 1677085516,
              updatedTs: 1677086654,
            },
            {
              ticketId: 731,
              barcodeMessage: "230540106030275000",
              status: "pickedup",
              createdTs: 1677085563,
              updatedTs: null,
            },
            {
              ticketId: 732,
              barcodeMessage: "230540106030275001",
              status: "redeemed",
              createdTs: 1677085563,
              updatedTs: 1677086783,
            },
            {
              ticketId: 733,
              barcodeMessage: "230540106030275002",
              status: "redeemed",
              createdTs: 1677085563,
              updatedTs: 1677086937,
            },
            {
              ticketId: 748,
              barcodeMessage: "231041058342217000",
              status: "pickedup",
              createdTs: 1681441114,
              updatedTs: null,
            },
            {
              ticketId: 749,
              barcodeMessage: "231041058342217001",
              status: "pickedup",
              createdTs: 1681441114,
              updatedTs: null,
            },
            {
              ticketId: 750,
              barcodeMessage: "231041058342217002",
              status: "pickedup",
              createdTs: 1681441114,
              updatedTs: null,
            },
            {
              ticketId: 751,
              barcodeMessage: "231041107552823000",
              status: "pickedup",
              createdTs: 1681441675,
              updatedTs: null,
            },
            {
              ticketId: 752,
              barcodeMessage: "231041107552823001",
              status: "redeemed",
              createdTs: 1681441675,
              updatedTs: 1681441787,
            },
            {
              ticketId: 753,
              barcodeMessage: "231041107552823002",
              status: "redeemed",
              createdTs: 1681441675,
              updatedTs: 1681441932,
            },
          ],
        },
      ];
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ data: result });
        }, 300);
      });
    },
    getTickets() {
      this.callGetTicketsAPI()
        // this.dummyTickets()
        .then((res) => {
          console.log("getTicketsResData", res.data);
          this.$store.commit("updateUserTickets", res.data);
          // this.tickets = res.data
          //   .reduce((acc, product) => {
          //     const tickets = product.tickets.map((ticket) => {
          //       const ticketCopy = { ...ticket };
          //       ticketCopy.title = product.title;
          //       ticketCopy.description = product.description;
          //       ticketCopy.storeName = product.storeName;
          //       ticketCopy.image = product.images[0];
          //       ticketCopy.useStartAt = product.useStartAt;
          //       ticketCopy.useExpirationAt = product.useExpirationAt;
          //       ticketCopy.updatedAt = ticket.updatedTs || ticket.createdTs;
          //       ticketCopy.duration = product.duration;
          //       return ticketCopy;
          //     });
          //     return (acc = [...acc, ...tickets]);
          //   }, [])
          //   .filter((t) => t.status === "pickedup")
          //   .sort((a, b) => a.updatedAt - b.updatedAt);
        })
        .catch((err) => {
          console.log("getTickets err:", err);
          this.$store.commit("updateUserTickets", []);
        })
        .finally(() => {
          this.ticketInitialized = true
        });
    },
    setSelectedStampCard(val) {
      this.selectedStampCard = val;
    }
  },
};
</script>

<style>
#carousel .slider-indicators {
  bottom: 5px;
}

#carousel .slider-indicator-icon {
  margin: 0 5px;
  border: 1px solid white;
  background-color: white;
  opacity: 1;
  box-shadow: 0px 5px 10px rgba(55, 84, 170, 0.2);
}

#carousel .slider-indicator-active {
  background-color: var(--k1);
}

#carousel {
  border-radius: 10px;
}

#menus .slider-btn {
  background: transparent;
  width: 18px;
  height: 18px;
  top: 50%;
  transform: translateY(-50%);
}
#menus .slider-btn .slider-icon {
  border-left: 2px solid var(--k1);
  border-bottom: 2px solid var(--k1);
  width: 13px;
  height: 13px;
  top: 50%;
}

#menus .slider-item {
  align-items: unset;
}
</style>
